/**
 * ONE OR TWO LANGUAGES (spec §7 P-7.1, §2 P-2.10, §9.1 I-10). Pure: no Vue, no store, no I/O.
 *
 * The account says which languages its customers write in (`customerLanguages`: `ar`, `en`
 * or `both`; absent is Both). Everything here reads that one setting:
 *
 *   - `optionLanguageProblems` / `optionLanguageWarnings`: the sidecar's
 *     `tasks/languages.ts`, mirrored. One language: an option with no words in it blocks the
 *     save. Both: an option written in one language only is a warning, never a block.
 *   - `menuOptionMissing`: the same one-language rule on a menu draft (`menus/languages.ts`).
 *   - `taskMissingWords`, `menuMissingWords`, `handoverMissingWords`: every bilingual field
 *     written in ONE language only, on a Both account, as rows the save-time warning lists and
 *     "Translate them for me" sends in one batch.
 *
 * A row is `{ key, kind, what, name, from, to, text, target }`:
 *   - `kind` is the translate kind the sidecar caps it by;
 *   - `from` is the language it is written in, `to` the one it is missing, `text` the words;
 *   - `target` is how the screen writes an answer back: `fieldFor(target, to)`.
 */

export const CUSTOMER_LANGUAGES = ['ar', 'en', 'both'];

/** The most items one "Translate them for me" sends: the sidecar's `TRANSLATE_BATCH_MAX`. */
export const TRANSLATE_BATCH_MAX = 20;

export const languagesOf = value =>
  CUSTOMER_LANGUAGES.includes(value) ? value : 'both';

export const isOneLanguage = languages =>
  languages === 'ar' || languages === 'en';

export const otherLangOf = lang => (lang === 'ar' ? 'en' : 'ar');

const words = value => (typeof value === 'string' ? value.trim() : '');

/** Which side of a pair is blank while the other is written, or `null`. */
export const missingSide = (en, ar) => {
  const hasEn = words(en) !== '';
  const hasAr = words(ar) !== '';
  if (hasEn === hasAr) return null;
  return hasEn ? 'ar' : 'en';
};

/** The field an answer in `lang` is written to. */
export const fieldFor = (target, lang) =>
  lang === 'ar' ? target.fieldAr : target.fieldEn;

const stepsOf = definition =>
  Array.isArray(definition?.steps) ? definition.steps.filter(Boolean) : [];

const optionsOf = step => (Array.isArray(step?.options) ? step.options : []);

/** `tasks/languages.ts` `optionLanguageProblems`: one language, a blank label blocks. */
export const optionLanguageProblems = (definition, languages) => {
  if (!isOneLanguage(languages)) return [];
  const field = languages === 'ar' ? 'labelAr' : 'labelEn';
  return stepsOf(definition).flatMap(step =>
    step.type !== 'choose'
      ? []
      : optionsOf(step)
          .filter(option => words(option?.[field]) === '')
          .map(option => ({
            stepId: step.id,
            problem: 'option_missing_label',
            detail: `${option.id}:${languages}`,
          }))
  );
};

/** `tasks/languages.ts` `optionLanguageWarnings`: Both, a half-written option warns. */
export const optionLanguageWarnings = (definition, languages) => {
  if (languages !== 'both') return [];
  return stepsOf(definition).flatMap(step =>
    step.type !== 'choose'
      ? []
      : optionsOf(step).flatMap(option => {
          const side = missingSide(option?.labelEn, option?.labelAr);
          return side === null
            ? []
            : [
                {
                  stepId: step.id,
                  warning: 'option_missing_language',
                  detail: `${option.id}:${side}`,
                },
              ];
        })
  );
};

/** The first menu option with no title in the account's one language, as an index, or -1. */
export const menuOptionMissing = (options, languages) => {
  if (!isOneLanguage(languages)) return -1;
  const field = languages === 'ar' ? 'titleAr' : 'titleEn';
  return (Array.isArray(options) ? options : []).findIndex(
    option => words(option?.[field]) === ''
  );
};

const pairRow = ({ key, kind, what, name, en, ar, target }) => {
  const to = missingSide(en, ar);
  if (to === null) return [];
  const from = otherLangOf(to);
  return [
    {
      key,
      kind,
      what,
      name: words(name),
      from,
      to,
      text: words(from === 'ar' ? ar : en),
      target,
    },
  ];
};

const QUESTION_TYPES = ['ask', 'choose', 'choose_live'];

/**
 * Every field of a task written in one language only, on a Both account.
 *
 * @param {object} definition  `{ steps }`, as `spineToDefinition` compiles it.
 * @param {{ en?: string, ar?: string }} cta  the form's button, off the draft.
 */
export const taskMissingWords = (definition, cta, languages) => {
  if (languages !== 'both') return [];
  const rows = stepsOf(definition).flatMap(step => {
    const own = [];
    if (QUESTION_TYPES.includes(step.type)) {
      own.push(
        ...pairRow({
          key: `${step.id}::question`,
          kind: 'task_question',
          what: 'question',
          name: step.name,
          en: step.questionEn,
          ar: step.questionAr,
          target: {
            stepId: step.id,
            optionId: null,
            fieldEn: 'questionEn',
            fieldAr: 'questionAr',
          },
        })
      );
    }
    if (step.type === 'choose') {
      optionsOf(step).forEach(option => {
        own.push(
          ...pairRow({
            key: `${step.id}:${option.id}:label`,
            kind: 'task_option_label',
            what: 'option',
            name: words(option.labelEn) || words(option.labelAr),
            en: option.labelEn,
            ar: option.labelAr,
            target: {
              stepId: step.id,
              optionId: option.id,
              fieldEn: 'labelEn',
              fieldAr: 'labelAr',
            },
          })
        );
      });
    }
    if (step.type === 'tell' && step.exactEn !== undefined) {
      own.push(
        ...pairRow({
          key: `${step.id}::message`,
          kind: 'task_message',
          what: 'message',
          name: step.name,
          en: step.exactEn,
          ar: step.exactAr,
          target: {
            stepId: step.id,
            optionId: null,
            fieldEn: 'exactEn',
            fieldAr: 'exactAr',
          },
        })
      );
    }
    return own;
  });
  return [
    ...rows,
    ...pairRow({
      key: '::cta',
      kind: 'task_cta',
      what: 'form_button',
      name: '',
      en: cta?.en,
      ar: cta?.ar,
      target: {
        stepId: '',
        optionId: null,
        fieldEn: 'ctaEn',
        fieldAr: 'ctaAr',
      },
    }),
  ];
};

/** Every field of a menu draft written in one language only, on a Both account. */
export const menuMissingWords = (draft, languages) => {
  if (languages !== 'both') return [];
  const options = (Array.isArray(draft?.options) ? draft.options : []).flatMap(
    option =>
      pairRow({
        key: `option:${option.key}`,
        kind: 'menu_row_title',
        what: 'option',
        name: words(option.titleEn) || words(option.titleAr),
        en: option.titleEn,
        ar: option.titleAr,
        target: {
          optionKey: option.key,
          fieldEn: 'titleEn',
          fieldAr: 'titleAr',
        },
      })
  );
  return [
    ...options,
    ...pairRow({
      key: 'body',
      kind: 'menu_body',
      what: 'menu_body',
      name: '',
      en: draft?.bodyEn,
      ar: draft?.bodyAr,
      target: { optionKey: null, fieldEn: 'bodyEn', fieldAr: 'bodyAr' },
    }),
    ...pairRow({
      key: 'button',
      kind: 'menu_button',
      what: 'menu_button',
      name: '',
      en: draft?.buttonEn,
      ar: draft?.buttonAr,
      target: { optionKey: null, fieldEn: 'buttonEn', fieldAr: 'buttonAr' },
    }),
  ];
};

/** Personality's handover line, when it is written in one language only (Both accounts). */
export const handoverMissingWords = (form, languages) =>
  languages !== 'both'
    ? []
    : pairRow({
        key: 'handover',
        kind: 'handover_line',
        what: 'handover',
        name: '',
        en: form?.handoffMessage,
        ar: form?.handoffMessageAr,
        target: { fieldEn: 'handoffMessage', fieldAr: 'handoffMessageAr' },
      });

/**
 * Personality's "When you're closed" line (P-10.6), when it is written in one language only
 * (Both accounts). Its own row, listed only while opening hours are set: without hours there is
 * no "closed" and the box is not on the page.
 */
export const closedLineMissingWords = (form, languages) =>
  languages !== 'both'
    ? []
    : pairRow({
        key: 'handover_closed',
        kind: 'handover_line',
        what: 'handover_closed',
        name: '',
        en: form?.handoverClosedEn,
        ar: form?.handoverClosedAr,
        target: { fieldEn: 'handoverClosedEn', fieldAr: 'handoverClosedAr' },
      });

/** The rows missing `lang`, at most one batch of them. */
export const batchFor = (rows, lang) =>
  rows.filter(row => row.to === lang).slice(0, TRANSLATE_BATCH_MAX);
